import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Sparkles, Zap } from 'lucide-react';

/**
 * PWAUpdater v3.0 — Banner de Atualização Forçado
 * - Aparece para TODOS os usuários quando há nova versão
 * - Auto-atualiza após 30 segundos
 * - Limpa o cache antigo antes de recarregar
 * - Verifica atualizações a cada 5 minutos
 */
export function PWAUpdater() {
 const [countdown, setCountdown] = useState(30);
 const [updating, setUpdating] = useState(false);
 const [minimized, setMinimized] = useState(false);

 const {
 needRefresh: [needRefresh, setNeedRefresh],
 updateServiceWorker,
 } = useRegisterSW({
 onRegistered(r) {
 console.log('✅ SW Registrado:', r);
 // Verifica atualizações a cada 5 minutos (antes era 1 hora)
 if (r) {
 setInterval(() => {
 console.log('🔍 Verificando atualizações do app...');
 r.update();
 }, 5 * 60 * 1000); // 5 minutos
 }
 },
 onRegisterError(error) {
 console.error('❌ SW registration error', error);
 },
 });

 // Contagem regressiva para auto-atualização
 useEffect(() => {
 if (!needRefresh || minimized) return;

 setCountdown(30);
 const timer = setInterval(() => {
 setCountdown(prev => {
 if (prev <= 1) {
 clearInterval(timer);
 handleUpdate();
 return 0;
 }
 return prev - 1;
 });
 }, 1000);

 return () => clearInterval(timer);
 }, [needRefresh, minimized]);

 const handleUpdate = async () => {
 if (updating) return;
 setUpdating(true);

 try {
 // 1. Limpar todos os caches antigos do Service Worker
 if ('caches' in window) {
 const cacheNames = await caches.keys();
 await Promise.all(cacheNames.map(name => caches.delete(name)));
 console.log('🧹 Caches limpos:', cacheNames);
 }

 // 2. Forçar o novo Service Worker a assumir o controle
 await updateServiceWorker(true);

 // 3. Recarregar a página com force-reload
 setTimeout(() => {
 window.location.reload();
 }, 500);
 } catch (err) {
 console.error('Erro ao atualizar:', err);
 window.location.reload();
 }
 };

 const progressPct = ((30 - countdown) / 30) * 100;

 return (
 <>
 <AnimatePresence>
 {needRefresh && !minimized && (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="fixed inset-0 z-[9999] flex items-end justify-center p-4 pb-8 md:items-center"
 style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}
 >
 <motion.div
 initial={{ y: 100, opacity: 0, scale: 0.9 }}
 animate={{ y: 0, opacity: 1, scale: 1 }}
 exit={{ y: 100, opacity: 0 }}
 transition={{ type: 'spring', damping: 20, stiffness: 200 }}
 className="w-full max-w-sm bg-[#0F0F10] border border-foreground/10 rounded-[32px] overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.8)]"
 >
 {/* Barra de progresso do countdown */}
 <div className="h-1 bg-foreground/10 w-full">
 <motion.div
 className="h-full bg-gradient-to-r from-orange-500 to-yellow-400"
 initial={{ width: '0%' }}
 animate={{ width: `${progressPct}%` }}
 transition={{ ease: 'linear', duration: 1 }}
 />
 </div>

 <div className="p-7">
 {/* Ícone animado */}
 <div className="flex items-center gap-4 mb-6">
 <div className="relative">
 <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-yellow-400 flex items-center justify-center shadow-[0_8px_24px_rgba(255,106,0,0.4)]">
 <Sparkles className="w-7 h-7 text-foreground" />
 </div>
 <motion.div
 animate={{ rotate: 360 }}
 transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
 className="absolute -top-1 -right-1 w-5 h-5 bg-green-400 rounded-full flex items-center justify-center"
 >
 <Zap className="w-3 h-3 text-black" />
 </motion.div>
 </div>
 <div>
 <p className="text-[10px] font-black text-orange-400 uppercase tracking-[0.2em] mb-0.5">
 Nova versão disponível
 </p>
 <h2 className="text-xl font-display font-black text-foreground leading-tight">
 App Atualizado! 🚀
 </h2>
 </div>
 </div>

 <p className="text-sm text-foreground/40 leading-relaxed mb-2">
 O app foi atualizado para a <strong>última versão</strong> com os novos planos VIP e melhorias de velocidade.
 </p>

 <p className="text-xs text-foreground/40 mb-6">
 Atualizando automaticamente em{' '}
 <span className="text-orange-400 font-black">{countdown}s</span>
 {' '}• Cache antigo será limpo
 </p>

 <button
 onClick={handleUpdate}
 disabled={updating}
 className="w-full h-14 rounded-2xl font-black uppercase tracking-widest text-sm relative overflow-hidden group transition-all"
 style={{
 background: 'linear-gradient(135deg, #FF6A00, #FFB347)',
 boxShadow: '0 8px 30px rgba(255,106,0,0.4)',
 }}
 >
 <span className="relative z-10 flex items-center justify-center gap-2 text-foreground">
 {updating ? (
 <>
 <RefreshCw className="w-5 h-5 animate-spin" />
 Atualizando...
 </>
 ) : (
 <>
 <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
 Atualizar Agora
 </>
 )}
 </span>
 </button>

 <button
 onClick={() => setMinimized(true)}
 className="w-full mt-3 text-xs text-foreground/40 hover:text-foreground/40 transition-colors py-2 font-bold uppercase tracking-widest"
 >
 Lembrar depois
 </button>
 </div>
 </motion.div>
 </motion.div>
 )}
 </AnimatePresence>

 <AnimatePresence>
 {needRefresh && minimized && (
 <motion.button
 initial={{ scale: 0, opacity: 0 }}
 animate={{ scale: 1, opacity: 1 }}
 exit={{ scale: 0, opacity: 0 }}
 onClick={() => setMinimized(false)}
 className="fixed bottom-6 right-6 z-[9998] w-14 h-14 bg-gradient-to-br from-orange-500 to-yellow-400 rounded-full flex items-center justify-center shadow-[0_8px_30px_rgba(255,106,0,0.4)] hover:scale-110 transition-transform"
 title="Atualização disponível"
 >
 <div className="relative">
 <RefreshCw className="w-6 h-6 text-foreground" />
 <motion.div
 animate={{ rotate: 360 }}
 transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
 className="absolute -top-2 -right-2 w-3 h-3 bg-green-400 rounded-full border-2 border-orange-500"
 />
 </div>
 </motion.button>
 )}
 </AnimatePresence>
 </>
 );
}
